import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  useWindowDimensions,
  Platform,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import * as DocumentPicker from 'expo-document-picker';
import Svg, { Path } from 'react-native-svg';
import { COLORS, FONTS } from '../../constants/config';
import { PageContainer, Navbar, Breadcrumb, KPIDeleteConfirmModal, MultiSelectDropdown } from '../../components';
import CancelConfirmModal from '../../components/CancelConfirmModal';
import { useCancelConfirm } from '../../hooks/useCancelConfirm';
import { useResponsive, useRoleNavigation, useAutoTranslate, useUnsavedChangesWarning } from '../../hooks';
import { queryClient, queryKeys } from '../../providers/QueryProvider';
import { extractKpiErrorMessage, validateKpiRequiredFields, validateKpiTargetConfigs } from '../../utils/kpiErrors';
import { normalizeArabicNumbers, getTextAlign } from '../../utils/rtl';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { subProcessesApi, processesApi, goalsApi, organizationalUnitsApi, kpisApi, processStepsApi, employeesApi } from '../../services/apiService';
import { StrategicGoalList, OrganizationalUnitList, ProcessList, KPIType, Polarity, Frequency, SubProcess, Employee } from '../../types';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';

import { tokens } from '@design';
import { Button } from '@design/components/Button';
import KpiAnnualTargetSection, {
  KpiTargetConfig,
  emptyKpiTargetConfig,
  buildKpiTargetPayload,
  configFromKpi,
} from '../kpis/components/KpiAnnualTargetSection';
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;
type RouteProps = RouteProp<MainStackParamList, 'AddSubProcess'>;

// ============================================================
// SVG Icons
// ============================================================
const ChevronLeftIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M15 18L9 12L15 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronDownIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M6 9L12 15L18 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronUpIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M18 15L12 9L6 15" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const PlusIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 5V19M5 12H19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const TrashIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M3 6H5H21" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const MenuIcon = ({ size = 16, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M4 12H20" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M4 6H20" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M4 18H20" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const InfoIcon = ({ size = 20, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 16V12" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 8H12.01" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// ============================================================
// Portal Dropdown Component (uses raw HTML divs for proper scroll handling)
// ============================================================
interface PortalDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  buttonRef: React.RefObject<View>;
  children: React.ReactNode;
  backgroundColor?: string;
  borderColor?: string;
}

const PortalDropdown = ({ isOpen, onClose, buttonRef, children, backgroundColor = tokens.colors.raw.white, borderColor = tokens.colors.raw.slate["200"] }: PortalDropdownProps) => {
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0, openUpward: false, availableHeight: 300 });

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      (buttonRef.current as any).measureInWindow((x: number, y: number, width: number, height: number) => {
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - (y + height + 4);
        const spaceAbove = y - 4;
        const maxDropdown = 300;
        const openUpward = spaceBelow < 150 && spaceAbove > spaceBelow;
        setPosition({
          top: openUpward ? Math.max(4, y - Math.min(maxDropdown, spaceAbove)) : y + height + 4,
          left: x,
          width: width,
          openUpward,
          availableHeight: openUpward ? Math.min(maxDropdown, spaceAbove) : Math.min(maxDropdown, spaceBelow),
        });
      });
    }
  }, [isOpen, buttonRef]);

  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleScroll = (e: Event) => {
      if (dropdownRef.current && dropdownRef.current.contains(e.target as Node)) return;
      onClose();
    };
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const dropdownMenu = (
    <>
      {/* eslint-disable-next-line */}
      <div
        onMouseDown={(e: any) => { e.preventDefault(); e.stopPropagation(); onClose(); }}
        style={{
          position: 'fixed' as const,
          inset: 0,
          zIndex: 9998,
        }}
      />
      <div
        ref={dropdownRef}
        onMouseDown={(e: any) => e.stopPropagation()}
        style={{
          position: 'fixed',
          top: position.top,
          insetInlineStart: position.left,
          width: position.width,
          backgroundColor: backgroundColor,
          border: `1px solid ${borderColor}`,
          borderRadius: tokens.radii.absolute["radius-8"],
          boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
          zIndex: 9999,
          maxHeight: position.availableHeight,
          overflowY: 'auto' as const,
        }}
      >
        {children}
      </div>
    </>
  );

  return ReactDOM.createPortal(dropdownMenu, document.body);
};

// Step Form Data Interface
interface StepFormData {
  id: string;
  order: string;
  name: string;
  description: string;
  responsible: string;
  organizational_units: number[];  // ManyToMany - multiple units
  responsible_employees: number[]; // ManyToMany - multiple employees
  duration: string;
  timeUnit: string;
  tools: string;
  isExpanded: boolean;
  documentFile?: DocumentPicker.DocumentPickerAsset | null;
}

// KPI Form Data Interface
interface KPIFormData {
  id: string;
  name: string;
  description: string;
  weight: string;
  kpiType: string;
  polarity: string;
  unit: string;
  targetValue: string;
  worstAcceptableValue: string;
  frequency: string;
  formula: string;
  dataSource: string;
  measurementStartDate: string;
  target: KpiTargetConfig;
  isExpanded: boolean;
  responsibleUnits: string[];
}

const createEmptyStep = (order: number): StepFormData => ({
  // Prefix so parseInt(id) is NaN — a digit-leading random id would be misread
  // as an existing row id and PATCHed to a foreign KPI/step (404 → payload-less
  // create → end-date 400). Mirrors AddGoalScreen.generateId.
  id: `new_${Math.random().toString(36).substr(2, 9)}`,
  order: order.toString(),
  name: '',
  description: '',
  responsible: '',
  organizational_units: [],  // ManyToMany - empty array
  responsible_employees: [], // ManyToMany - empty array
  duration: '',
  timeUnit: '',
  tools: '',
  isExpanded: true,
});

const createEmptyKPI = (): KPIFormData => ({
  // Prefix so parseInt(id) is NaN — a digit-leading random id would be misread
  // as an existing row id and PATCHed to a foreign KPI/step (404 → payload-less
  // create → end-date 400). Mirrors AddGoalScreen.generateId.
  id: `new_${Math.random().toString(36).substr(2, 9)}`,
  name: '',
  description: '',
  weight: '',
  kpiType: '',
  polarity: '',
  unit: '%',
  // New KPIs author the target through the annual grid; the scalar stays at the
  // '0.0' sentinel so the required-field check passes (matches every other inline
  // KPI screen). '' here made the grid-authored target fail validation → the
  // "fill all required fields" error on save. See utils/kpiErrors.ts.
  targetValue: '0.0',
  worstAcceptableValue: '',
  frequency: 'yearly',
  formula: '',
  dataSource: '',
  measurementStartDate: '',
  target: emptyKpiTargetConfig,
  isExpanded: true,
  responsibleUnits: [],
});

export default function AddSubProcessScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const processId = route.params?.processId;
  const subProcessId = route.params?.subProcessId;
  const scrollToKpi = route.params?.scrollToKpi;
  const { navigateToHome, isViewOnly } = useRoleNavigation();
  const isEditMode = !!subProcessId;
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const styles = useMemo(() => getStyles(isRTL), [isRTL]);
  const { translate, translateUnit } = useAutoTranslate();

  // Theme colors for dark mode support
  const themeColors = {
    mainCard: colors.card,
    mainCardBorder: colors.cardBorder,
    pageTitle: colors.textPrimary,
    section: isDark ? colors.backgroundAlt : tokens.colors.raw.slate["100"],
    sectionBorder: colors.border,
    sectionTitle: colors.textPrimary,
    label: colors.textPrimary,
    input: colors.card,
    inputBorder: colors.border,
    inputText: colors.textPrimary,
    placeholder: isDark ? tokens.colors.raw.gray["500"] : tokens.colors.raw.gray["400"],
    iconColor: colors.textMuted,
    dropdown: colors.card,
    dropdownBorder: colors.border,
    dropdownText: isDark ? tokens.colors.raw.gray["500"] : tokens.colors.raw.gray["400"],
    dropdownTextSelected: colors.textPrimary,
    dropdownMenu: colors.card,
    dropdownMenuBorder: colors.border,
    dropdownItem: colors.textPrimary,
    dropdownItemBorder: isDark ? colors.border : tokens.colors.raw.gray["100"],
    stepCard: colors.card,
    stepCardBorder: colors.cardBorder,
    stepHeader: colors.card,
    stepHeaderTitle: colors.textPrimary,
    stepNumberBadge: isDark ? colors.backgroundAlt : tokens.colors.raw.slate["200"],
    kpiCard: colors.card,
    kpiCardBorder: colors.cardBorder,
    kpiHeader: colors.card,
    kpiHeaderTitle: colors.textPrimary,
    kpiNumberBadge: isDark ? colors.backgroundAlt : tokens.colors.raw.slate["200"],
    kpiSubsection: isDark ? colors.backgroundAlt : tokens.colors.raw.slate["100"],
    weightInputWrapper: colors.card,
    weightButton: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.1)',
    weightButtonBorder: isDark ? '#4B5563' : tokens.colors.raw.slate["300"],
    addButton: colors.card,
    addButtonBorder: isDark ? '#4B5563' : tokens.colors.raw.slate["300"],
    addButtonText: colors.textPrimary,
    loadingText: colors.textMuted,
    dateInput: colors.card,
    dateInputBorder: colors.border,
  };

  // Redirect view-only users (executive) - they cannot add/edit
  useEffect(() => {
    if (isViewOnly) {
      console.log('🚫 AddSubProcess: View-only user - redirecting');
      if (navigation.canGoBack()) {
        navigation.goBack();
      } else {
        navigation.navigate('Processes' as any);
      }
    }
  }, [isViewOnly, navigation]);

  // Refs for scrolling
  const scrollViewRef = useRef<ScrollView>(null);
  const kpiSectionRef = useRef<View>(null);
  const [kpiSectionY, setKpiSectionY] = useState<number>(0);

  const { width } = useWindowDimensions();
  const { showSuccess, showError } = useNotification();
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [goals, setGoals] = useState<StrategicGoalList[]>([]);
  const [units, setUnits] = useState<OrganizationalUnitList[]>([]);
  const [processes, setProcesses] = useState<ProcessList[]>([]);

  // Track if fields are inherited from parent process (should be readonly)
  const [isInheritedFromParent, setIsInheritedFromParent] = useState(false);
  const [parentProcessName, setParentProcessName] = useState<string>('');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    strategicGoals: [] as number[],
    parentProcess: processId || null as number | null,
    organizationalUnits: [] as number[],
    // تفاصيل العملية fields
    employeesCount: '',
    weight: '',
    processTime: '',
    timeUnit: '',
    executionFrequency: '',
    processCost: '',
    approvalPerson: '',
    processFlowImages: '',
  });

  useUnsavedChangesWarning(!!formData.name || !!formData.description);

  const [steps, setSteps] = useState<StepFormData[]>([createEmptyStep(1)]);
  const [kpis, setKpis] = useState<KPIFormData[]>([]);
  const [kpiPendingDelete, setKpiPendingDelete] = useState<{ id: string; backendId: number | null; name: string } | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedFile, setSelectedFile] = useState<DocumentPicker.DocumentPickerAsset | null>(null);
  const [existingFileUrl, setExistingFileUrl] = useState<string | null>(null);

  // Employees per step (keyed by step.id)
  const [stepEmployees, setStepEmployees] = useState<Record<string, Employee[]>>({});
  const [loadingEmployees, setLoadingEmployees] = useState<Record<string, boolean>>({});

  // Step dropdowns state (keyed by step.id)
  const [activeStepUnitDropdown, setActiveStepUnitDropdown] = useState<string | null>(null);
  const [activeStepEmployeeDropdown, setActiveStepEmployeeDropdown] = useState<string | null>(null);
  const [activeStepTimeUnitDropdown, setActiveStepTimeUnitDropdown] = useState<string | null>(null);
  const [stepUnitSearch, setStepUnitSearch] = useState<Record<string, string>>({});
  const [stepEmployeeSearch, setStepEmployeeSearch] = useState<Record<string, string>>({});

  // Dropdowns
  const [showGoalDropdown, setShowGoalDropdown] = useState(false);
  const [showProcessDropdown, setShowProcessDropdown] = useState(false);
  const [showClassificationDropdown, setShowClassificationDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showTimeUnitDropdown, setShowTimeUnitDropdown] = useState(false);
  const [showFrequencyDropdown, setShowFrequencyDropdown] = useState(false);
  const [activeKpiDropdown, setActiveKpiDropdown] = useState<{ kpiId: string; type: string } | null>(null);

  // Dropdown trigger refs for portal positioning
  const goalDropdownRef = useRef<View>(null);
  const processDropdownRef = useRef<View>(null);
  const timeUnitDropdownRef = useRef<View>(null);
  const frequencyDropdownRef = useRef<View>(null);


  // Dropdown position state (for non-PortalDropdown dropdowns)
  const [timeUnitDropdownPos, setTimeUnitDropdownPos] = useState({ top: 0, left: 0, width: 0 });

  const isSmallScreen = width < 768;

  // Dropdown options
  const classificationOptions = [
    { value: 'main', label: t('common.main') },
    { value: 'sub', label: t('subProcesses.sub') },
  ];

  const statusOptions = [
    { value: 'active', label: t('common.active') },
    { value: 'inactive', label: t('common.inactive') },
    { value: 'draft', label: t('common.draft') },
  ];

  // KPI type options
  const kpiTypeOptions = [
    { value: 'strategic', label: t('kpis.strategic') },
    { value: 'operational', label: t('kpis.operational') },
    { value: 'efficiency', label: t('kpis.efficiency') },
    { value: 'effectiveness', label: t('kpis.effectiveness') },
    { value: 'impact', label: t('kpis.impact') },
  ];

  const polarityOptions = [
    { value: 'positive', label: t('kpis.ascending') },
    { value: 'negative', label: t('kpis.descending') },
  ];

  const unitOptions = [
    { value: '%', label: t('kpis.percentage') },
    { value: 'number', label: t('kpis.number') },
    { value: 'currency', label: t('kpis.currency') },
  ];

  const frequencyOptions = [
    { value: 'daily', label: t('kpis.daily') },
    { value: 'weekly', label: t('kpis.weekly') },
    { value: 'monthly', label: t('kpis.monthly') },
    { value: 'quarterly', label: t('kpis.quarterly') },
    { value: 'half_yearly', label: t('kpis.semiAnnual') },
    { value: 'yearly', label: t('kpis.annual') },
  ];

  // Time unit options
  const timeUnitOptions = [
    { value: 'minute', label: t('kpis.minute') },
    { value: 'hour', label: t('kpis.hour') },
    { value: 'day', label: t('kpis.day') },
    { value: 'week', label: t('kpis.week') },
    { value: 'month', label: t('kpis.month') },
    { value: 'year', label: t('kpis.year') },
  ];

  // Execution frequency options (دورية التنفيذ)
  const executionFrequencyOptions = [
    { value: 'daily', label: t('kpis.daily') || 'يومي' },
    { value: 'weekly', label: t('kpis.weekly') || 'أسبوعي' },
    { value: 'monthly', label: t('kpis.monthly') },
    { value: 'quarterly', label: t('kpis.quarterly') },
    { value: 'half_yearly', label: t('kpis.semiAnnual') },
    { value: 'yearly', label: t('kpis.annual') },
  ];

  // File picker handler for step document template
  const handleStepFilePick = async (stepId: string) => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ['image/*', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
        copyToCacheDirectory: true,
      });
      if (!result.canceled && result.assets && result.assets.length > 0) {
        setSteps(steps.map(s => s.id === stepId ? { ...s, documentFile: result.assets[0] } : s));
      }
    } catch (error) {
      console.error('Error picking step file:', error);
      showError(t('readings.filePickFailed'));
    }
  };

  // File picker handler
  const handleFilePick = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ['image/*', 'application/pdf'],
        copyToCacheDirectory: true,
      });

      if (!result.canceled && result.assets && result.assets.length > 0) {
        setSelectedFile(result.assets[0]);
      }
    } catch (error) {
      console.error('Error picking file:', error);
      showError(t('readings.filePickFailed'));
    }
  };

  const toggleKpiDropdown = (kpiId: string, dropdownType: string) => {
    if (activeKpiDropdown?.kpiId === kpiId && activeKpiDropdown?.type === dropdownType) {
      setActiveKpiDropdown(null);
    } else {
      setActiveKpiDropdown({ kpiId, type: dropdownType });
    }
    closeAllDropdowns();
  };

  const closeAllDropdowns = () => {
    setShowGoalDropdown(false);
    setShowProcessDropdown(false);
    setShowClassificationDropdown(false);
    setShowStatusDropdown(false);
    setShowTimeUnitDropdown(false);
    setShowFrequencyDropdown(false);
    // Also close step dropdowns
    setActiveStepUnitDropdown(null);
    setActiveStepEmployeeDropdown(null);
    setActiveStepTimeUnitDropdown(null);
  };

  // Helper function to measure dropdown button position
  // Handle dropdown open
  const handleOpenGoalDropdown = () => {
    setShowGoalDropdown(!showGoalDropdown);
    setShowProcessDropdown(false);
    setActiveKpiDropdown(null);
  };

  const handleOpenProcessDropdown = () => {
    setShowProcessDropdown(!showProcessDropdown);
    setShowGoalDropdown(false);
    setActiveKpiDropdown(null);
  };

  const handleOpenTimeUnitDropdown = () => {
    setShowTimeUnitDropdown(!showTimeUnitDropdown);
    setShowGoalDropdown(false);
    setShowProcessDropdown(false);
    setActiveKpiDropdown(null);
  };


  useEffect(() => {
    fetchData();
  }, [subProcessId]);

  // Handle scrollToKpi - auto-add a KPI form and scroll to the section
  useEffect(() => {
    if (scrollToKpi && !loading) {
      // Add a new KPI form if there are no KPIs yet
      setTimeout(() => {
        if (kpis.length === 0) {
          setKpis([createEmptyKPI()]);
        }
        // Scroll to the KPI section
        if (scrollViewRef.current && kpiSectionY > 0) {
          scrollViewRef.current.scrollTo({ y: kpiSectionY - 100, animated: true });
        }
      }, 300);
    }
  }, [scrollToKpi, loading, kpiSectionY]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [goalsData, unitsData, processesData] = await Promise.all([
        goalsApi.list({ page_size: 100 }),
        organizationalUnitsApi.list({ page_size: 100 }),
        processesApi.list({ page_size: 100 }),
      ]);
      setGoals(goalsData.results || []);
      setUnits(unitsData.results || []);
      setProcesses(processesData.results || []);

      // If editing, fetch the sub-process data
      if (subProcessId) {
        const subProcessData = await subProcessesApi.get(subProcessId);
        setFormData({
          name: subProcessData.name || '',
          description: subProcessData.description || '',
          strategicGoals: subProcessData.strategic_goals || [],
          parentProcess: subProcessData.process || null,
          organizationalUnits: subProcessData.organizational_units || [],
          weight: subProcessData.weight?.toString() || '',
          employeesCount: subProcessData.employees_count?.toString() || '',
          processTime: subProcessData.process_time?.toString() || '',
          timeUnit: subProcessData.time_unit || '',
          executionFrequency: subProcessData.execution_frequency || '',
          processCost: subProcessData.cost?.toString() || '',
          approvalPerson: subProcessData.approval_person || '',
          processFlowImages: '',
        });

        // Set existing file URL if available
        if (subProcessData.process_flow_diagram) {
          setExistingFileUrl(subProcessData.process_flow_diagram);
        }

        // Load existing steps from subProcessData.steps
        if (subProcessData.steps && subProcessData.steps.length > 0) {
          const loadedSteps: StepFormData[] = subProcessData.steps.map((step: any) => ({
            id: step.id.toString(),
            order: step.order || 1,
            name: step.name || '',
            description: step.description || '',
            responsible: step.responsible || '',
            // ManyToMany fields - ensure arrays
            organizational_units: step.organizational_units || [],
            responsible_employees: step.responsible_employees || [],
            duration: step.duration || '',
            timeUnit: step.time_unit || '',
            tools: step.tools || '',
            isExpanded: false,
          }));
          setSteps(loadedSteps);

          // Fetch employees for each step that has organizational units
          loadedSteps.forEach((step) => {
            if (step.organizational_units && step.organizational_units.length > 0) {
              fetchEmployeesForStep(step.id, step.organizational_units);
            }
          });
        }

        // Fetch existing KPIs for this sub-process
        const existingKpis = await kpisApi.list({ source_sub_process: subProcessId, page_size: 100 });
        if (existingKpis.results && existingKpis.results.length > 0) {
          const loadedKpis: KPIFormData[] = existingKpis.results.map((kpi: any) => ({
            id: kpi.id.toString(),
            name: kpi.name || '',
            description: kpi.description || '',
            weight: kpi.weight?.toString() || '',
            kpiType: kpi.kpi_type || '',
            polarity: kpi.polarity || '',
            unit: kpi.unit || '',
            targetValue: kpi.target_value?.toString() || '',
            worstAcceptableValue: kpi.worst_acceptable_value != null ? String(kpi.worst_acceptable_value) : '',
            frequency: kpi.frequency || '',
            formula: kpi.measurement_formula || '',
            dataSource: kpi.data_source || '',
            measurementStartDate: kpi.measurement_start_date || '',
            target: configFromKpi(kpi),
            isExpanded: false,
            responsibleUnits: (kpi.responsible_units || []).map((id: number) => String(id)),
          }));
          setKpis(loadedKpis);
        }
      }
      // If adding new subprocess with parent process ID, fetch parent process data and auto-fill
      else if (processId && !subProcessId) {
        try {
          const parentProcess = await processesApi.get(processId);
          setIsInheritedFromParent(true);
          setParentProcessName(parentProcess.name || '');

          // Inherit structural fields from parent process
          // Name and description left empty for user to fill
          setFormData(prev => ({
            ...prev,
            parentProcess: parentProcess.id,                   // Inherited, disabled
            strategicGoals: parentProcess.strategic_goals || [], // Inherited default (editable)
            organizationalUnits: parentProcess.organizational_units || [], // Inherited from all parent units
          }));
        } catch (err) {
          console.error('Failed to fetch parent process:', err);
          // Still set the processId even if fetch fails
          setFormData(prev => ({
            ...prev,
            parentProcess: processId,
          }));
        }
      }
    } catch (err) {
      showError(t('processes.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('validation.processNameRequired');
    }
    if (!formData.parentProcess) {
      newErrors.parentProcess = t('validation.mainProcessRequired');
    }
    if (formData.strategicGoals.length === 0) {
      newErrors.strategicGoals = t('validation.strategicGoalRequired');
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return false;

    const kpiError = validateKpiRequiredFields(kpis, t, 'measurementStartDate');
    if (kpiError) { showError(kpiError); return false; }
    // Inline target-config validation (end date, pattern/step values) — catch an
    // invalid setup here instead of letting the server 400 it on save.
    const kpiTargetError = validateKpiTargetConfigs(kpis, t, 'measurementStartDate');
    if (kpiTargetError) { showError(kpiTargetError); return false; }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showError(t('validation.fillRequiredFields'));
      return;
    }

    setSubmitting(true);
    try {
      // Prepare data - use FormData if file is selected, otherwise use regular object
      let subProcessData: Partial<SubProcess> | FormData;
      let savedSubProcessId: number;

      if (selectedFile) {
        // Use FormData for file upload
        const formDataPayload = new FormData();
        formDataPayload.append('process', String(formData.parentProcess));
        formDataPayload.append('name', formData.name);
        formDataPayload.append('description', formData.description || '');
        formData.strategicGoals.forEach(gid => formDataPayload.append('strategic_goals', String(gid)));
        formData.organizationalUnits.forEach(uid => formDataPayload.append('organizational_units', String(uid)));
        formDataPayload.append('weight', formData.weight || '0');
        if (formData.processTime) formDataPayload.append('process_time', formData.processTime);
        if (formData.timeUnit) formDataPayload.append('time_unit', formData.timeUnit);
        if (formData.employeesCount) formDataPayload.append('employees_count', formData.employeesCount);
        if (formData.executionFrequency) formDataPayload.append('execution_frequency', formData.executionFrequency);
        if (formData.processCost) formDataPayload.append('cost', formData.processCost);
        if (formData.approvalPerson) formDataPayload.append('approval_person', formData.approvalPerson);

        // Add file
        if (Platform.OS === 'web') {
          const response = await fetch(selectedFile.uri);
          const blob = await response.blob();
          const file = new File([blob], selectedFile.name || 'diagram.pdf', {
            type: selectedFile.mimeType || 'application/pdf',
          });
          console.log('📎 File upload (web):', {
            name: file.name,
            type: file.type,
            size: file.size,
          });
          formDataPayload.append('process_flow_diagram', file);
        } else {
          const fileData = {
            uri: selectedFile.uri,
            type: selectedFile.mimeType || 'application/octet-stream',
            name: selectedFile.name || 'diagram.pdf',
          };
          console.log('📎 File upload (native):', fileData);
          formDataPayload.append('process_flow_diagram', fileData as any);
        }

        console.log('📤 Sending FormData with file attachment');
        subProcessData = formDataPayload;
      } else {
        // Use regular object for no file
        subProcessData = {
          process: formData.parentProcess!,
          name: formData.name,
          description: formData.description || '',
          strategic_goals: formData.strategicGoals,
          organizational_units: formData.organizationalUnits,
          weight: formData.weight || '0',
          process_time: formData.processTime ? parseInt(formData.processTime, 10) : null,
          time_unit: formData.timeUnit || '',
          employees_count: formData.employeesCount ? parseInt(formData.employeesCount, 10) : null,
          execution_frequency: formData.executionFrequency || '',
          cost: formData.processCost ? parseFloat(formData.processCost) : null,
          approval_person: formData.approvalPerson || '',
        };
      }

      if (isEditMode && subProcessId) {
        await subProcessesApi.update(subProcessId, subProcessData);
        savedSubProcessId = subProcessId;
      } else {
        const createdSubProcess = await subProcessesApi.create(subProcessData);
        savedSubProcessId = createdSubProcess.id;
      }

      // Save steps (خطوات العملية)
      if (steps.length > 0) {
        const stepPromises = steps
          .filter(step => step.name.trim())
          .map(async (step, index) => {
            const stepData = {
              sub_process: savedSubProcessId,
              order: index + 1,
              name: step.name,
              description: step.description || '',
              responsible: step.responsible || '',
              // ManyToMany fields - send arrays of IDs
              organizational_units: step.organizational_units || [],
              responsible_employees: step.responsible_employees || [],
              duration: step.duration || '',
              time_unit: step.timeUnit || '',
              tools: step.tools || '',
            };

            // Check if this is an existing step (numeric ID) or new one (random string ID)
            const numericId = parseInt(step.id, 10);
            if (!isNaN(numericId) && numericId > 0) {
              // Try to update existing step, fall back to create if not found
              try {
                return await processStepsApi.update(numericId, stepData);
              } catch (err: any) {
                if (err.response?.status === 404) {
                  // Step doesn't exist, create new one
                  return processStepsApi.create(stepData);
                }
                throw err;
              }
            } else {
              // Create new step
              return processStepsApi.create(stepData);
            }
          });

        await Promise.all(stepPromises);
      }

      // Handle KPIs - create new ones and update existing ones
      const kpiErrors: string[] = [];
      const validKpis = kpis.filter(kpi => kpi.name.trim());
      for (const kpi of validKpis) {
        const kpiData: any = {
          name: kpi.name,
          description: kpi.description || '',
          source_sub_process: savedSubProcessId,
          kpi_type: (kpi.kpiType || 'operational') as KPIType,
          polarity: (kpi.polarity || 'positive') as Polarity,
          unit: kpi.unit || 'number',
          target_value: (parseFloat(kpi.targetValue) || 0).toFixed(2),
          frequency: (kpi.frequency || 'monthly') as Frequency,
          measurement_formula: kpi.formula || '',
          data_source: kpi.dataSource || '',
          weight: kpi.weight || '0',
          source_type: 'sub_process',
          responsible_units: kpi.responsibleUnits.map(Number),
        };

        if (kpi.measurementStartDate && kpi.measurementStartDate.trim()) {
          kpiData.measurement_start_date = kpi.measurementStartDate;
        } else {
          kpiData.measurement_start_date = null;
        }

        const numericId = parseInt(kpi.id, 10);
        if (!isNaN(numericId) && numericId > 0) {
          try {
            await kpisApi.update(numericId, {
              ...kpiData,
              ...buildKpiTargetPayload(kpi.target, {
                unit: kpi.unit,
                frequency: kpi.frequency as Frequency | '',
                startDate: kpi.measurementStartDate,
              }),
            });
          } catch (err: any) {
            if (err.response?.status === 404) {
              try {
                const created = await kpisApi.create({
                  ...kpiData,
                  ...buildKpiTargetPayload(kpi.target, {
                    unit: kpi.unit,
                    frequency: kpi.frequency as Frequency | '',
                    startDate: kpi.measurementStartDate,
                  }),
                });
                // Swap the stale id for the real one so a save retry updates this
                // KPI instead of creating a duplicate on the sub-process.
                setKpis(prev => prev.map(k => (k.id === kpi.id ? { ...k, id: String(created.id) } : k)));
              } catch (createError: any) {
                const errMsg = extractKpiErrorMessage(createError?.response?.data);
                kpiErrors.push(t('kpis.kpiSaveError', { name: kpi.name, error: errMsg }));
              }
            } else {
              const errMsg = extractKpiErrorMessage(err?.response?.data);
              kpiErrors.push(t('kpis.kpiUpdateError', { name: kpi.name, error: errMsg }));
            }
          }
        } else {
          try {
            const created = await kpisApi.create({
              ...kpiData,
              ...buildKpiTargetPayload(kpi.target, {
                unit: kpi.unit,
                frequency: kpi.frequency as Frequency | '',
                startDate: kpi.measurementStartDate,
              }),
            });
            // Swap the temp id for the real one so a save retry updates this KPI
            // instead of creating a duplicate on the sub-process.
            setKpis(prev => prev.map(k => (k.id === kpi.id ? { ...k, id: String(created.id) } : k)));
          } catch (createError: any) {
            const errMsg = extractKpiErrorMessage(createError?.response?.data);
            kpiErrors.push(t('kpis.kpiSaveError', { name: kpi.name, error: errMsg }));
          }
        }
      }

      if (kpiErrors.length > 0) {
        // A KPI save failed — surface it and STOP. Do NOT show success or
        // navigate away: that masked the failure and silently discarded the
        // user's KPI input (entered values "disappeared"). Keep them on the
        // form with their data so they can fix and retry.
        showError(kpiErrors.join('\n'));
        return;
      }

      showSuccess(isEditMode ? t('subProcesses.updateSuccess') : t('subProcesses.createSuccess'));
      queryClient.invalidateQueries({ queryKey: queryKeys.processes.all });
      // Go to the saved sub-process's detail page instead of goBack()/list.
      if (savedSubProcessId) {
        navigation.navigate('SubProcessDetail', { id: savedSubProcessId });
      } else if (navigation.canGoBack()) {
        navigation.goBack();
      } else {
        navigation.navigate('Processes' as any);
      }
    } catch (err: any) {
      console.error('Error saving sub-process:', err);
      console.error('Error response data:', err.response?.data);
      console.error('Error response status:', err.response?.status);
      let errorMessage = isEditMode ? t('subProcesses.updateFailed') : t('subProcesses.createFailed');
      if (err.response?.data) {
        const data = err.response.data;
        if (typeof data === 'string') {
          errorMessage = data;
        } else if (data.detail) {
          errorMessage = data.detail;
        } else if (typeof data === 'object') {
          // Extract field-specific errors
          const fieldErrors = Object.entries(data)
            .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`)
            .join('; ');
          if (fieldErrors) {
            errorMessage = fieldErrors;
            console.error('Field errors:', fieldErrors);
          }
        }
      }
      showError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.navigate('Processes' as any);
    }
  };

  const { requestCancel, confirmVisible, confirmDiscard, dismissConfirm } = useCancelConfirm({
    snapshot: { formData, kpis, steps, selectedFile },
    ready: !loading,
    onExit: handleCancel,
  });

  // Step handlers
  const handleAddStep = () => {
    setSteps([...steps, createEmptyStep(steps.length + 1)]);
  };

  const handleRemoveStep = async (stepId: string) => {
    if (steps.length > 1) {
      // Check if this is an existing step (numeric ID)
      const numericId = parseInt(stepId, 10);
      if (!isNaN(numericId) && numericId > 0) {
        try {
          await processStepsApi.delete(numericId);
          showSuccess(t('subProcesses.stepDeleteSuccess'));
        } catch (err) {
          showError(t('subProcesses.stepDeleteFailed'));
          return;
        }
      }
      setSteps(steps.filter(s => s.id !== stepId));
    }
  };

  const handleStepChange = (stepId: string, field: keyof StepFormData, value: string) => {
    setSteps(steps.map(s => s.id === stepId ? { ...s, [field]: value } : s));
  };

  const toggleStepExpand = (stepId: string) => {
    setSteps(steps.map(s => s.id === stepId ? { ...s, isExpanded: !s.isExpanded } : s));
  };

  // Fetch employees for multiple organizational units
  const fetchEmployeesForStep = async (stepId: string, unitIds: number[]) => {
    if (!unitIds || unitIds.length === 0) {
      setStepEmployees(prev => ({ ...prev, [stepId]: [] }));
      return;
    }

    setLoadingEmployees(prev => ({ ...prev, [stepId]: true }));

    try {
      // Use comma-separated IDs for multiple units filter
      const response = await employeesApi.list({ organizational_units: unitIds.join(',') });
      setStepEmployees(prev => ({
        ...prev,
        [stepId]: Array.isArray(response) ? response : (response.results || [])
      }));
    } catch (error) {
      console.error('Error fetching employees:', error);
      setStepEmployees(prev => ({ ...prev, [stepId]: [] }));
    } finally {
      setLoadingEmployees(prev => ({ ...prev, [stepId]: false }));
    }
  };

  // Toggle organizational unit selection (multi-select)
  const handleStepUnitToggle = (stepId: string, unitId: number) => {
    setSteps(steps.map(s => {
      if (s.id !== stepId) return s;

      const currentUnits = s.organizational_units || [];
      let newUnits: number[];

      if (currentUnits.includes(unitId)) {
        // Remove unit
        newUnits = currentUnits.filter(id => id !== unitId);
      } else {
        // Add unit
        newUnits = [...currentUnits, unitId];
      }

      // Remove employees that are no longer in selected units
      const newEmployees = s.responsible_employees.filter(empId => {
        const emp = stepEmployees[stepId]?.find(e => e.id === empId);
        return emp && newUnits.includes(emp.organizational_unit || 0);
      });

      return { ...s, organizational_units: newUnits, responsible_employees: newEmployees };
    }));

    // Get updated units and fetch employees
    const step = steps.find(s => s.id === stepId);
    if (step) {
      const currentUnits = step.organizational_units || [];
      const newUnits = currentUnits.includes(unitId)
        ? currentUnits.filter(id => id !== unitId)
        : [...currentUnits, unitId];
      fetchEmployeesForStep(stepId, newUnits);
    }
  };

  // Toggle employee selection (multi-select)
  const handleStepEmployeeToggle = (stepId: string, employeeId: number) => {
    setSteps(steps.map(s => {
      if (s.id !== stepId) return s;

      const currentEmployees = s.responsible_employees || [];
      let newEmployees: number[];

      if (currentEmployees.includes(employeeId)) {
        // Remove employee
        newEmployees = currentEmployees.filter(id => id !== employeeId);
      } else {
        // Add employee
        newEmployees = [...currentEmployees, employeeId];
      }

      return { ...s, responsible_employees: newEmployees };
    }));
  };

  // KPI handlers
  const handleAddKPI = () => {
    setKpis([...kpis, createEmptyKPI()]);
  };

  const handleRemoveKPI = (kpiId: string) => {
    const target = kpis.find(k => k.id === kpiId);
    const numericId = parseInt(kpiId, 10);
    const backendId = !isNaN(numericId) && numericId > 0 ? numericId : null;
    setKpiPendingDelete({
      id: kpiId,
      backendId,
      name: (target as any)?.name || '',
    });
  };

  const confirmRemoveKPI = async () => {
    if (!kpiPendingDelete) return;
    const { id, backendId } = kpiPendingDelete;
    if (backendId !== null) {
      try {
        await kpisApi.delete(backendId);
        showSuccess(t('kpis.deleteSuccess'));
      } catch (err) {
        showError(t('kpis.deleteFailed'));
        setKpiPendingDelete(null);
        return;
      }
    }
    setKpis(prev => prev.filter(k => k.id !== id));
    setKpiPendingDelete(null);
  };

  const handleKPIChange = (kpiId: string, field: keyof KPIFormData, value: string) => {
    setKpis(kpis.map(k => k.id === kpiId ? { ...k, [field]: value } : k));
  };

  const toggleKPIExpand = (kpiId: string) => {
    setKpis(kpis.map(k => k.id === kpiId ? { ...k, isExpanded: !k.isExpanded } : k));
  };

  const orgUnitOptions = units.map(u => ({ value: u.id.toString(), label: translateUnit(u.name_ar || u.name) }));

  if (loading) {
    return (
      <PageContainer>
        <Navbar />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={COLORS.primary} />
          <Text style={[styles.loadingText, { color: themeColors.loadingText }]}>{t('common.loadingData')}</Text>
        </View>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Navbar />
      <ScrollView ref={scrollViewRef} style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
        <View style={[styles.container, isSmallScreen && styles.containerSmall]}>
          {/* Breadcrumb */}
          <Breadcrumb
            items={[
              { label: t('navigation.home') },
              { label: t('navigation.processes'), route: 'Processes' },
              { label: isEditMode ? t('processes.editSubProcess') : t('processes.addSubProcess') },
            ]}
          />

          {/* Main Card Container */}
          <View style={[styles.mainCard, { backgroundColor: themeColors.mainCard, borderColor: themeColors.mainCardBorder, overflow: 'visible', zIndex: 10 }]}>
          {/* Title */}
          <Text style={[styles.pageTitle, { color: themeColors.pageTitle }]}>{isEditMode ? t('processes.editSubProcess') : t('processes.addSubProcess')}</Text>

          {/* Basic Information Section */}
          <View style={[styles.section, { backgroundColor: themeColors.section, borderColor: themeColors.sectionBorder, zIndex: (showGoalDropdown || showProcessDropdown) ? 9999 : 30, overflow: 'visible' }]}>
            <Text style={[styles.sectionTitle, { color: themeColors.sectionTitle }]}>{t('common.basicInfo')}</Text>

            <View style={[styles.formRow, isSmallScreen && styles.formRowSmall, { zIndex: showGoalDropdown ? 9999 : 10, overflow: 'visible' }]}>
              {/* Process Name - Simple input without button */}
              <View style={[styles.formGroup, styles.formGroupHalf]}>
                <Text style={[styles.label, { color: themeColors.label }]}>
                  {t('processes.processName')} <Text style={styles.required}>*</Text>
                </Text>
                <TextInput
                  style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }, errors.name ? styles.inputError : undefined]}
                  value={formData.name}
                  onChangeText={(text) => setFormData({ ...formData, name: text })}
                  placeholder=""
                  placeholderTextColor={themeColors.placeholder}
                  textAlign="right"
                />
              </View>

              {/* Strategic Goals - multi-select (prefilled from parent, editable) */}
              <View style={[styles.formGroup, styles.formGroupHalf, { zIndex: showGoalDropdown ? 9999 : 1, overflow: 'visible' }]}>
                <Text style={[styles.label, { color: themeColors.label }]}>
                  {t('goals.strategicGoals')} <Text style={styles.required}>*</Text>
                  {isInheritedFromParent && <Text style={styles.inheritedLabel}> ({t('common.inherited')})</Text>}
                </Text>
                <TouchableOpacity
                  ref={goalDropdownRef}
                  data-dropdown-trigger="true"
                  style={[styles.dropdown, { backgroundColor: themeColors.dropdown, borderColor: themeColors.dropdownBorder, minHeight: 44, height: 'auto' as any, flexWrap: 'wrap' as any }, errors.strategicGoals ? styles.inputError : undefined]}
                  onPress={handleOpenGoalDropdown}
                >
                  <ChevronDownIcon size={16} color={themeColors.iconColor} />
                  {formData.strategicGoals.length > 0 ? (
                    <View style={{ flex: 1, flexDirection: 'row-reverse', flexWrap: 'wrap', gap: tokens.spacing.absolute["1_5"] }}>
                      {formData.strategicGoals.map(gid => {
                        const goal = goals.find(g => g.id === gid);
                        return (
                          <View key={gid} style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: tokens.colors.raw.red["50"], borderRadius: tokens.radii.absolute["radius-12"], paddingHorizontal: tokens.spacing.absolute["2"], paddingVertical: tokens.spacing.absolute["1"] }}>
                            <TouchableOpacity
                              onPress={(e) => {
                                e.stopPropagation();
                                setFormData({ ...formData, strategicGoals: formData.strategicGoals.filter(id => id !== gid) });
                              }}
                              style={{ marginStart: tokens.spacing.absolute["1"] }}
                            >
                              <Text style={{ color: tokens.colors.semantic.general["Main Color"], fontSize: tokens.typography.paragraph["paragraph small font size"], fontWeight: '700' }}>×</Text>
                            </TouchableOpacity>
                            <Text style={{ color: tokens.colors.semantic.general["Main Color"], fontSize: tokens.typography.paragraph["paragraph mini font size"], fontFamily: 'JF-Flat-Regular' }} numberOfLines={1}>
                              {translate(goal?.title) || `#${gid}`}
                            </Text>
                          </View>
                        );
                      })}
                    </View>
                  ) : (
                    <Text style={[styles.dropdownText, { color: themeColors.dropdownText }]}>
                      {t('goals.selectStrategicGoal')}
                    </Text>
                  )}
                </TouchableOpacity>
                <PortalDropdown
                  isOpen={showGoalDropdown}
                  onClose={() => setShowGoalDropdown(false)}
                  buttonRef={goalDropdownRef}
                  backgroundColor={themeColors.dropdownMenu}
                  borderColor={themeColors.dropdownMenuBorder}
                >
                  <ScrollView style={{ maxHeight: 200 }} nestedScrollEnabled>
                    {goals.length === 0 ? (
                      <View style={[styles.dropdownItem, { borderBottomColor: themeColors.dropdownItemBorder }]}>
                        <Text style={[styles.dropdownItemText, { color: themeColors.dropdownItem }]}>{t('common.noData')}</Text>
                      </View>
                    ) : (
                      goals.map(goal => {
                        const isSelected = formData.strategicGoals.includes(goal.id);
                        return (
                          <TouchableOpacity
                            key={goal.id}
                            style={[styles.dropdownItem, { borderBottomColor: themeColors.dropdownItemBorder }, isSelected && { backgroundColor: tokens.colors.raw.red["50"] }]}
                            onPress={() => {
                              if (isSelected) {
                                setFormData({ ...formData, strategicGoals: formData.strategicGoals.filter(id => id !== goal.id) });
                              } else {
                                setFormData({ ...formData, strategicGoals: [...formData.strategicGoals, goal.id] });
                              }
                              setErrors({ ...errors, strategicGoals: '' });
                            }}
                          >
                            <View style={{ width: 20, height: 20, borderRadius: tokens.radii.absolute["radius-4"], borderWidth: 1.5, borderColor: isSelected ? tokens.colors.semantic.general["Main Color"] : tokens.colors.raw.gray["300"], backgroundColor: isSelected ? tokens.colors.semantic.general["Main Color"] : 'transparent', alignItems: 'center', justifyContent: 'center', marginStart: tokens.spacing.absolute["2"] }}>
                              {isSelected && <Text style={{ color: '#FFF', fontSize: tokens.typography.paragraph["paragraph mini font size"], fontWeight: '700' }}>✓</Text>}
                            </View>
                            <Text style={[styles.dropdownItemText, { color: themeColors.dropdownItem }, isSelected && { fontWeight: '600' }]}>{translate(goal.title)}</Text>
                          </TouchableOpacity>
                        );
                      })
                    )}
                  </ScrollView>
                </PortalDropdown>
              </View>
            </View>

            {/* Description - الهدف من العملية */}
            <View style={styles.formGroup}>
              <Text style={[styles.label, { color: themeColors.label }]}>
                {t('processes.processDescription')} <Text style={styles.required}>*</Text>
              </Text>
              <TextInput
                style={[styles.input, styles.textArea, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                value={formData.description}
                onChangeText={(text) => setFormData({ ...formData, description: text })}
                placeholder=""
                placeholderTextColor={themeColors.placeholder}
                textAlign="right"
                multiline
                numberOfLines={3}
              />
            </View>

            <View style={[styles.formRow, isSmallScreen && styles.formRowSmall, { zIndex: showProcessDropdown ? 9999 : 20, overflow: 'visible' }]}>
              {/* Parent Process - Disabled when inherited from parent */}
              <View style={[styles.formGroup, styles.formGroupHalf, { zIndex: showProcessDropdown ? 9999 : 2, overflow: 'visible' }]}>
                <Text style={styles.label}>
                  {t('subProcesses.mainProcess')} <Text style={styles.required}>*</Text>
                  {isInheritedFromParent && <Text style={styles.inheritedLabel}> ({t('common.inherited')})</Text>}
                </Text>
                <TouchableOpacity
                  ref={processDropdownRef}
                  data-dropdown-trigger="true"
                  style={[styles.dropdown, isInheritedFromParent && styles.dropdownDisabled, errors.parentProcess ? styles.inputError : undefined]}
                  disabled={isInheritedFromParent}
                  onPress={() => {
                    if (!isInheritedFromParent) {
                      handleOpenProcessDropdown();
                    }
                  }}
                >
                  <ChevronDownIcon size={16} color={isInheritedFromParent ? tokens.colors.raw.gray["400"] : tokens.colors.raw.gray["500"]} />
                  <Text style={[styles.dropdownText, formData.parentProcess ? styles.dropdownTextSelected : undefined]}>
                    {formData.parentProcess
                      ? (isInheritedFromParent && parentProcessName ? translate(parentProcessName) : translate(processes.find(p => p.id === formData.parentProcess)?.name) || t('processes.selectMainProcess'))
                      : t('processes.selectMainProcess')}
                  </Text>
                </TouchableOpacity>
                {!isInheritedFromParent && (
                  <PortalDropdown
                    isOpen={showProcessDropdown}
                    onClose={() => setShowProcessDropdown(false)}
                    buttonRef={processDropdownRef}
                    backgroundColor={themeColors.dropdownMenu}
                    borderColor={themeColors.dropdownMenuBorder}
                  >
                    {processes.map(process => (
                      <TouchableOpacity
                        key={process.id}
                        style={[styles.dropdownItem, { borderBottomColor: themeColors.dropdownItemBorder }]}
                        onPress={() => {
                          setFormData({ ...formData, parentProcess: process.id });
                          setShowProcessDropdown(false);
                        }}
                      >
                        <Text style={[styles.dropdownItemText, { color: themeColors.dropdownItem }]}>{translate(process.name)}</Text>
                      </TouchableOpacity>
                    ))}
                  </PortalDropdown>
                )}
              </View>

              {/* Organizational Units - Multi-select, disabled when inherited from parent */}
              <View style={[styles.formGroup, styles.formGroupHalf]}>
                <Text style={styles.label}>
                  {t('initiatives.implementationResponsibility')} <Text style={styles.required}>*</Text>
                  {isInheritedFromParent && <Text style={styles.inheritedLabel}> ({t('common.inherited')})</Text>}
                </Text>
                <MultiSelectDropdown
                  values={formData.organizationalUnits.map(String)}
                  options={units.map(u => ({ value: u.id.toString(), label: translateUnit(u.name_ar || u.name) }))}
                  placeholder={t('common.selectResponsible')}
                  disabled={isInheritedFromParent}
                  onChange={(vals) => setFormData({ ...formData, organizationalUnits: vals.map(Number) })}
                />
              </View>
            </View>

            {/* Row 4: زمن العملية | وحدة الزمن */}
            <View style={[styles.formRow, isSmallScreen && styles.formRowSmall, { zIndex: showTimeUnitDropdown ? 9999 : 5, overflow: 'visible' }]}>
              <View style={[styles.formGroup, styles.formGroupHalf]}>
                <Text style={[styles.label, { color: themeColors.label }]}>{t('subProcesses.processTime')}</Text>
                <TextInput
                  style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                  value={formData.processTime}
                  onChangeText={(text) => {
                    const intValue = normalizeArabicNumbers(text).replace(/[^0-9]/g, '');
                    setFormData({ ...formData, processTime: intValue });
                  }}
                  placeholder="1"
                  placeholderTextColor={themeColors.placeholder}
                  textAlign="right"
                  keyboardType="numeric"
                />
              </View>
              <View style={[styles.formGroup, styles.formGroupHalf, { zIndex: showTimeUnitDropdown ? 9999 : 1, overflow: 'visible' }]}>
                <Text style={[styles.label, { color: themeColors.label }]}>{t('subProcesses.timeUnitLabel')}</Text>
                <TouchableOpacity
                  ref={timeUnitDropdownRef}
                  data-dropdown-trigger="true"
                  style={[styles.dropdown, { backgroundColor: themeColors.dropdown, borderColor: themeColors.dropdownBorder }]}
                  onPress={() => {
                    handleOpenTimeUnitDropdown();
                  }}
                >
                  <ChevronDownIcon size={16} color={themeColors.iconColor} />
                  <Text style={[styles.dropdownText, { color: themeColors.dropdownText }, formData.timeUnit ? { color: themeColors.dropdownTextSelected } : undefined]}>
                    {formData.timeUnit
                      ? timeUnitOptions.find(o => o.value === formData.timeUnit)?.label || t('subProcesses.selectTimeUnit')
                      : t('subProcesses.selectTimeUnit')}
                  </Text>
                </TouchableOpacity>
                {showTimeUnitDropdown && (
                  <View
                    data-dropdown-menu="true"
                    style={[styles.dropdownMenu, { backgroundColor: themeColors.dropdownMenu, borderColor: themeColors.dropdownMenuBorder }]}
                  >
                    <ScrollView style={{ maxHeight: 200 }} nestedScrollEnabled>
                      {timeUnitOptions.map(option => (
                        <TouchableOpacity
                          key={option.value}
                          style={[styles.dropdownItem, { borderBottomColor: themeColors.dropdownItemBorder }]}
                          onPress={() => {
                            setFormData({ ...formData, timeUnit: option.value });
                            setShowTimeUnitDropdown(false);
                          }}
                        >
                          <Text style={[styles.dropdownItemText, { color: themeColors.dropdownItem }]}>{option.label}</Text>
                        </TouchableOpacity>
                      ))}
                    </ScrollView>
                  </View>
                )}
              </View>
            </View>

          </View>

          {/* Process Details Section - تفاصيل العملية */}
          <View style={[styles.section, { backgroundColor: themeColors.section, borderColor: themeColors.sectionBorder, zIndex: 15, overflow: 'visible' }]}>
            <Text style={[styles.sectionTitle, { color: themeColors.sectionTitle }]}>{t('processes.processDetails')}</Text>

            {/* Row 1: دورية التنفيذ | عدد الموظفين المنفذين للعملية */}
            <View style={[styles.formRow, isSmallScreen && styles.formRowSmall, { zIndex: showFrequencyDropdown ? 9999 : 5, overflow: 'visible' }]}>
              <View style={[styles.formGroup, styles.formGroupHalf, { zIndex: showFrequencyDropdown ? 9999 : 1, overflow: 'visible', position: 'relative' as const }]}>
                <Text style={[styles.label, { color: themeColors.label }]}>{t('processes.executionFrequency')}</Text>
                <TouchableOpacity
                  ref={frequencyDropdownRef}
                  data-dropdown-trigger="true"
                  style={[styles.dropdown, { backgroundColor: themeColors.dropdown, borderColor: themeColors.dropdownBorder }]}
                  onPress={() => {
                    setShowFrequencyDropdown(!showFrequencyDropdown);
                    setShowTimeUnitDropdown(false);
                  }}
                >
                  <ChevronDownIcon size={16} color={themeColors.iconColor} />
                  <Text style={[styles.dropdownText, { color: themeColors.dropdownText }, formData.executionFrequency ? { color: themeColors.dropdownTextSelected } : undefined]}>
                    {formData.executionFrequency
                      ? executionFrequencyOptions.find(o => o.value === formData.executionFrequency)?.label || formData.executionFrequency
                      : t('subProcesses.selectExecutionFrequency')}
                  </Text>
                </TouchableOpacity>
                {showFrequencyDropdown && (
                  <View
                    data-dropdown-menu="true"
                    style={[styles.dropdownMenu, { backgroundColor: themeColors.dropdownMenu, borderColor: themeColors.dropdownMenuBorder, maxHeight: 250, overflow: 'auto' as any }]}
                  >
                    {executionFrequencyOptions.map(option => (
                      <TouchableOpacity
                        key={option.value}
                        style={[styles.dropdownItem, { borderBottomColor: themeColors.dropdownItemBorder }]}
                        onPress={() => {
                          setFormData({ ...formData, executionFrequency: option.value });
                          setShowFrequencyDropdown(false);
                        }}
                      >
                        <Text style={[styles.dropdownItemText, { color: themeColors.dropdownItem }]}>{option.label}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                )}
              </View>
              <View style={[styles.formGroup, styles.formGroupHalf]}>
                <Text style={[styles.label, { color: themeColors.label }]}>{t('subProcesses.executionEmployeesCount')}</Text>
                <TextInput
                  style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                  value={formData.employeesCount}
                  onChangeText={(text) => setFormData({ ...formData, employeesCount: normalizeArabicNumbers(text) })}
                  placeholder="1"
                  placeholderTextColor={themeColors.placeholder}
                  textAlign="right"
                  keyboardType="numeric"
                />
              </View>
            </View>

            {/* Row 2: تكلفة العملية | الاعتماد */}
            <View style={[styles.formRow, isSmallScreen && styles.formRowSmall]}>
              <View style={[styles.formGroup, styles.formGroupHalf]}>
                <Text style={[styles.label, { color: themeColors.label }]}>{t('subProcesses.processCost')}</Text>
                <View style={[styles.inputWithSuffixContainer, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, borderWidth: tokens.spacing.absolute["px"], borderRadius: tokens.radii.absolute["radius-8"], shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 2, elevation: 1 }]}>
                  <Text style={[styles.inputSuffixLeft, { color: themeColors.placeholder }]}>AED</Text>
                  <TextInput
                    style={[styles.inputFlex, { textAlign: getTextAlign(isRTL), color: themeColors.inputText }]}
                    value={formData.processCost}
                    onChangeText={(text) => setFormData({ ...formData, processCost: text })}
                    placeholder="1.0"
                    placeholderTextColor={themeColors.placeholder}
                    keyboardType="numeric"
                  />
                </View>
              </View>
              <View style={[styles.formGroup, styles.formGroupHalf]}>
                <Text style={[styles.label, { color: themeColors.label }]}>{t('subProcesses.approvalPerson')}</Text>
                <TextInput
                  style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                  value={formData.approvalPerson}
                  onChangeText={(text) => setFormData({ ...formData, approvalPerson: text })}
                  placeholder={t('subProcesses.approvalPersonPlaceholder')}
                  placeholderTextColor={themeColors.placeholder}
                  textAlign="right"
                />
              </View>
            </View>

            {/* Row 3: صورة المسار التدفقي للعملية (full width) */}
            <View style={styles.formGroup}>
              <Text style={[styles.label, { color: themeColors.label }]}>{t('subProcesses.processFlowImages')}</Text>
              <TouchableOpacity style={[styles.fileInput, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder }]} onPress={handleFilePick}>
                <View style={styles.fileInputContent}>
                  <Text style={[styles.fileInputText, { color: themeColors.inputText }]}>
                    {selectedFile?.name || (existingFileUrl ? existingFileUrl.split('/').pop() : 'Filename.pdf')}
                  </Text>
                  <View style={styles.fileInputButton}>
                    <Text style={styles.fileInputButtonText}>{t('common.chooseFile')}</Text>
                  </View>
                </View>
              </TouchableOpacity>
              <View style={styles.fileHelpText}>
                <Text style={[styles.fileHelpTextContent, { color: themeColors.label }]}>
                  {t('subProcesses.flowImagesHelp')}
                </Text>
                <InfoIcon size={20} color={themeColors.label} />
              </View>
            </View>
          </View>

          {/* Process Steps Section */}
          <View style={[styles.section, { zIndex: 10, borderRadius: tokens.radii.absolute["radius-4"], padding: tokens.spacing.absolute["3"], borderWidth: tokens.spacing.absolute["px"], borderColor: tokens.colors.raw.slate["200"], overflow: 'visible' as const }]}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>{t('subProcesses.processSteps')}</Text>
            </View>

            {steps.map((step, index) => (
              <View key={step.id} style={styles.stepCardContainer}>
                <View style={[styles.stepCard, { backgroundColor: themeColors.stepCard, borderColor: themeColors.stepCardBorder }]}>
                  {/* Step Header */}
                  <View style={styles.stepHeader}>
                    {/* Left Side - Action Buttons */}
                    <View style={styles.stepHeaderLeft}>
                      <TouchableOpacity
                        style={styles.stepIconButton}
                        onPress={() => toggleStepExpand(step.id)}
                      >
                        {step.isExpanded ? (
                          <ChevronUpIcon size={16} color={tokens.colors.semantic.general.foreground} />
                        ) : (
                          <ChevronDownIcon size={16} color={tokens.colors.semantic.general.foreground} />
                        )}
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={styles.stepIconButton}
                        onPress={() => handleRemoveStep(step.id)}
                      >
                        <TrashIcon size={16} color={tokens.colors.semantic.general.foreground} />
                      </TouchableOpacity>
                    </View>
                    {/* Right Side - Step Number + Name (when collapsed) */}
                    <View style={styles.stepHeaderRight}>
                      {!step.isExpanded && step.name ? (
                        <Text style={styles.stepCollapsedName} numberOfLines={1}>{step.name}</Text>
                      ) : null}
                      <View style={styles.stepNumber}>
                        <Text style={styles.stepNumberText}>{index + 1}</Text>
                      </View>
                    </View>
                  </View>

                  {step.isExpanded && (
                    <View style={styles.stepContent}>
                      {/* Row 1: الإجراء (Procedure) - Full width textarea */}
                      <View style={{ marginBottom: tokens.spacing.absolute["0"] }}>
                        <Text style={styles.label}>{t('subProcesses.stepProcedure')}</Text>
                        <TextInput
                          style={[styles.input, styles.textArea]}
                          value={step.name}
                          onChangeText={(text) => handleStepChange(step.id, 'name', text)}
                          placeholder=""
                          placeholderTextColor={tokens.colors.raw.slate["500"]}
                          textAlign="right"
                          multiline
                          numberOfLines={2}
                        />
                      </View>

                      {/* Row 2: المدة (right) + وحدة الزمن (left) - RTL row-reverse: first child = right */}
                      <View style={[styles.formRow, { marginBottom: tokens.spacing.absolute["0"], zIndex: 100 }, isSmallScreen && styles.formRowSmall]}>
                        <View style={[styles.formGroupHalf, { marginBottom: tokens.spacing.absolute["0"] }]}>
                          <Text style={styles.label}>{t('processes.duration')}</Text>
                          <TextInput
                            style={styles.input}
                            value={step.duration}
                            onChangeText={(text) => handleStepChange(step.id, 'duration', text)}
                            placeholder="1"
                            placeholderTextColor={tokens.colors.raw.slate["500"]}
                            textAlign="right"
                            keyboardType="numeric"
                          />
                        </View>
                        <View style={[styles.formGroupHalf, { zIndex: 1002, marginBottom: tokens.spacing.absolute["0"], position: 'relative' as const }]}>
                          <Text style={styles.label}>{t('processes.timeUnit')}</Text>
                          <TouchableOpacity
                            style={styles.dropdown}
                            onPress={() => {
                              setActiveStepTimeUnitDropdown(activeStepTimeUnitDropdown === step.id ? null : step.id);
                              setActiveStepUnitDropdown(null);
                              setActiveStepEmployeeDropdown(null);
                            }}
                          >
                            <View style={styles.dropdownContent}>
                              <ChevronDownIcon size={16} color={tokens.colors.semantic.general.foreground} />
                              <Text style={[
                                styles.dropdownText,
                                step.timeUnit ? styles.dropdownTextSelected : null,
                              ]}>
                                {step.timeUnit
                                  ? timeUnitOptions.find(o => o.value === step.timeUnit)?.label || t('subProcesses.selectTimeUnit')
                                  : t('subProcesses.selectTimeUnit')}
                              </Text>
                            </View>
                          </TouchableOpacity>
                          {activeStepTimeUnitDropdown === step.id && (
                            <View style={[styles.dropdownMenu, { zIndex: 9999 }]}>
                              <ScrollView style={{ maxHeight: 200 }} nestedScrollEnabled>
                                {timeUnitOptions.map(option => (
                                  <TouchableOpacity
                                    key={option.value}
                                    style={styles.dropdownItem}
                                    onPress={() => {
                                      handleStepChange(step.id, 'timeUnit', option.value);
                                      setActiveStepTimeUnitDropdown(null);
                                    }}
                                  >
                                    <Text style={[
                                      styles.dropdownItemText,
                                      step.timeUnit === option.value && { color: tokens.colors.semantic.general["Main Color"], fontFamily: FONTS.bold }
                                    ]}>
                                      {option.label}
                                    </Text>
                                  </TouchableOpacity>
                                ))}
                              </ScrollView>
                            </View>
                          )}
                        </View>
                      </View>

                      {/* Row 3: المسؤول (right half) - RTL row-reverse: first child = right */}
                      <View style={[styles.formRow, { marginBottom: tokens.spacing.absolute["0"], zIndex: 1 }, isSmallScreen && styles.formRowSmall]}>
                        <View style={[styles.formGroupHalf, { marginBottom: tokens.spacing.absolute["0"] }]}>
                          <Text style={styles.label}>{t('processes.responsible')}</Text>
                          <TextInput
                            style={styles.input}
                            value={step.responsible}
                            onChangeText={(text) => handleStepChange(step.id, 'responsible', text)}
                            placeholder={t('subProcesses.addResponsiblePlaceholder')}
                            placeholderTextColor={tokens.colors.raw.slate["500"]}
                            textAlign="right"
                          />
                        </View>
                        <View style={[styles.formGroupHalf, { marginBottom: tokens.spacing.absolute["0"] }]} />
                      </View>
                    </View>
                  )}
                </View>

                {/* Drag Handle - Right side in RTL */}
                <View style={styles.dragHandle}>
                  <MenuIcon size={16} color={tokens.colors.semantic.general.foreground} />
                </View>
              </View>
            ))}

            <TouchableOpacity style={styles.addStepButton} onPress={handleAddStep}>
              <Text style={styles.addStepButtonText}>{t('subProcesses.addNewStep')}</Text>
              <PlusIcon size={13} color={tokens.colors.semantic.general.foreground} />
            </TouchableOpacity>
          </View>

          {/* KPIs Section */}
          <View
            ref={kpiSectionRef}
            style={[styles.section, { zIndex: 5 }]}
            onLayout={(event) => {
              const { y } = event.nativeEvent.layout;
              setKpiSectionY(y);
            }}
          >
            <View style={styles.kpiSectionHeader}>
              <TouchableOpacity style={styles.sectionToggleButton}>
                {kpis.length > 0 ? (
                  <ChevronUpIcon size={16} color={tokens.colors.semantic.general.foreground} />
                ) : (
                  <ChevronDownIcon size={16} color={tokens.colors.semantic.general.foreground} />
                )}
              </TouchableOpacity>
              <Text style={styles.sectionTitleNoMargin}>{t('kpis.performanceIndicators')}</Text>
            </View>

            {kpis.map((kpi, index) => (
              <View key={kpi.id} style={styles.kpiCardContainer}>
                <View style={[styles.kpiCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
                  {/* KPI Header */}
                  <View style={styles.kpiHeader}>
                    {/* Left Side - Action Buttons */}
                    <View style={styles.kpiHeaderLeft}>
                      <TouchableOpacity
                        style={styles.stepIconButton}
                        onPress={() => toggleKPIExpand(kpi.id)}
                      >
                        {kpi.isExpanded ? (
                          <ChevronUpIcon size={16} color={tokens.colors.semantic.general.foreground} />
                        ) : (
                          <ChevronDownIcon size={16} color={tokens.colors.semantic.general.foreground} />
                        )}
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={[styles.stepIconButton, styles.stepIconButtonDisabled]}
                        onPress={() => handleRemoveKPI(kpi.id)}
                      >
                        <TrashIcon size={16} color={tokens.colors.semantic.general.foreground} />
                      </TouchableOpacity>
                    </View>
                    {/* Right Side - KPI Number and Name */}
                    <View style={styles.kpiHeaderRight}>
                      {!kpi.isExpanded && kpi.name && (
                        <Text style={styles.kpiCollapsedName}>{kpi.name}</Text>
                      )}
                      <View style={styles.kpiNumberBadge}>
                        <Text style={styles.kpiNumberBadgeText}>{index + 1}</Text>
                      </View>
                    </View>
                  </View>

                  {kpi.isExpanded && (
                    <View style={styles.kpiContent}>
                      {/* المعلومات الأساسية */}
                      <View style={styles.kpiSubsection}>
                        <Text style={styles.subsectionTitle}>{t('common.basicInfo')}</Text>

                        {/* Row 1: اسم المؤشر (left) | الوزن with button (right) - Figma layout */}
                        <View style={[styles.formRow, isSmallScreen && styles.formRowSmall]}>
                          {/* اسم المؤشر - left side (appears first in row-reverse) */}
                          <View style={[styles.formGroup, styles.formGroupHalf]}>
                            <Text style={styles.label}>
                              {t('kpis.kpiName')} <Text style={styles.required}>*</Text>
                            </Text>
                            <TextInput
                              style={styles.input}
                              value={kpi.name}
                              onChangeText={(text) => handleKPIChange(kpi.id, 'name', text)}
                              placeholder=""
                              placeholderTextColor={tokens.colors.raw.gray["400"]}
                              textAlign="right"
                            />
                          </View>
                          {/* الوزن - right side with تعيين الوزن button (appears second in row-reverse) */}
                          <View style={[styles.formGroup, styles.formGroupHalf]}>
                            <Text style={styles.label}>
                              {t('common.weight')} <Text style={styles.required}>*</Text>
                            </Text>
                            <View style={styles.inputWithButtonLeft}>
                              <View style={styles.inputWithSuffixContainer}>
                                <Text style={styles.inputSuffixLeft}>%</Text>
                                <TextInput
                                  style={[styles.inputFlex, { textAlign: getTextAlign(isRTL) }]}
                                  value={kpi.weight}
                                  onChangeText={(text) => handleKPIChange(kpi.id, 'weight', normalizeArabicNumbers(text))}
                                  placeholder="30"
                                  placeholderTextColor={tokens.colors.semantic.general.foreground}
                                  keyboardType="numeric"
                                />
                              </View>
                              <TouchableOpacity style={styles.assignWeightButton}>
                                <Text style={styles.assignWeightButtonText}>{t('kpis.setWeight')}</Text>
                              </TouchableOpacity>
                            </View>
                          </View>
                        </View>

                        {/* وصف المؤشر - Full width textarea */}
                        <View style={styles.formGroup}>
                          <Text style={styles.label}>
                            {t('kpis.kpiDescription')} <Text style={styles.required}>*</Text>
                          </Text>
                          <TextInput
                            style={[styles.input, styles.textAreaSmall]}
                            value={kpi.description}
                            onChangeText={(text) => handleKPIChange(kpi.id, 'description', text)}
                            placeholder=""
                            placeholderTextColor={tokens.colors.raw.gray["400"]}
                            textAlign="right"
                            multiline
                            numberOfLines={2}
                          />
                        </View>
                      </View>

                      {/* التصنيف - Figma layout: تصنيف المؤشر (right) | النمطية (left) */}
                      <View style={[styles.kpiSubsection, { zIndex: 20, overflow: 'visible' }]}>
                        <Text style={styles.subsectionTitle}>{t('common.classification')}</Text>

                        <View style={[styles.formRow, isSmallScreen && styles.formRowSmall, { zIndex: 100, overflow: 'visible' }]}>
                          {/* النمطية */}
                          <View style={[styles.formGroup, styles.formGroupHalf, { zIndex: activeKpiDropdown?.kpiId === kpi.id && activeKpiDropdown?.type === 'polarity' ? 9999 : 1, overflow: 'visible' }]}>
                            <Text style={styles.label}>
                              {t('kpis.polarity')} <Text style={styles.required}>*</Text>
                            </Text>
                            <TouchableOpacity
                              style={styles.dropdown}
                              onPress={() => toggleKpiDropdown(kpi.id, 'polarity')}
                            >
                              <ChevronDownIcon size={16} color={tokens.colors.semantic.general.foreground} />
                              <Text style={[styles.dropdownText, kpi.polarity ? styles.dropdownTextSelected : undefined]}>
                                {kpi.polarity
                                  ? polarityOptions.find(p => p.value === kpi.polarity)?.label || t('kpis.selectPolarity')
                                  : t('kpis.selectPolarity')}
                              </Text>
                            </TouchableOpacity>
                            {activeKpiDropdown?.kpiId === kpi.id && activeKpiDropdown?.type === 'polarity' && (
                              <View style={styles.dropdownMenu}>
                                <ScrollView style={{ maxHeight: 200 }} nestedScrollEnabled>
                                  {polarityOptions.map(option => (
                                    <TouchableOpacity
                                      key={option.value}
                                      style={styles.dropdownItem}
                                      onPress={() => {
                                        handleKPIChange(kpi.id, 'polarity', option.value);
                                        setActiveKpiDropdown(null);
                                      }}
                                    >
                                      <Text style={styles.dropdownItemText}>{option.label}</Text>
                                    </TouchableOpacity>
                                  ))}
                                </ScrollView>
                              </View>
                            )}
                          </View>
                        </View>

                        {/* KPI Type Row */}
                        <View style={[styles.formRow, isSmallScreen && styles.formRowSmall, { zIndex: 99, overflow: 'visible' }]}>
                          <View style={[styles.formGroup, styles.formGroupHalf, { zIndex: activeKpiDropdown?.kpiId === kpi.id && activeKpiDropdown?.type === 'kpiType' ? 9999 : 1, overflow: 'visible' }]}>
                            <Text style={styles.label}>
                              {t('kpis.kpiType')} <Text style={styles.required}>*</Text>
                            </Text>
                            <TouchableOpacity
                              style={styles.dropdown}
                              onPress={() => toggleKpiDropdown(kpi.id, 'kpiType')}
                            >
                              <ChevronDownIcon size={16} color={tokens.colors.semantic.general.foreground} />
                              <Text style={[styles.dropdownText, kpi.kpiType ? styles.dropdownTextSelected : undefined]}>
                                {kpi.kpiType
                                  ? kpiTypeOptions.find(o => o.value === kpi.kpiType)?.label || t('kpis.selectKpiType')
                                  : t('kpis.selectKpiType')}
                              </Text>
                            </TouchableOpacity>
                            {activeKpiDropdown?.kpiId === kpi.id && activeKpiDropdown?.type === 'kpiType' && (
                              <View style={styles.dropdownMenu}>
                                <ScrollView style={{ maxHeight: 200 }} nestedScrollEnabled>
                                  {kpiTypeOptions.map(option => (
                                    <TouchableOpacity
                                      key={option.value}
                                      style={styles.dropdownItem}
                                      onPress={() => {
                                        handleKPIChange(kpi.id, 'kpiType', option.value);
                                        setActiveKpiDropdown(null);
                                      }}
                                    >
                                      <Text style={styles.dropdownItemText}>{option.label}</Text>
                                    </TouchableOpacity>
                                  ))}
                                </ScrollView>
                              </View>
                            )}
                          </View>
                        </View>

                        <View style={{ marginTop: tokens.spacing.absolute['3'], zIndex: 5 }}>
                          <Text style={[styles.label, { marginBottom: 8 }]}>{t('initiatives.implementationResponsibility')}</Text>
                          <MultiSelectDropdown
                            values={kpi.responsibleUnits}
                            options={(() => {
                              const related = formData.organizationalUnits.map(String);
                              const opts = orgUnitOptions.filter(o => related.includes(o.value));
                              kpi.responsibleUnits.forEach(v => {
                                if (!opts.find(o => o.value === v)) {
                                  const found = orgUnitOptions.find(o => o.value === v);
                                  if (found) opts.push(found);
                                }
                              });
                              return opts;
                            })()}
                            placeholder={t('initiatives.selectResponsibleUnits')}
                            onChange={(vals) => setKpis(prev => prev.map(k => k.id === kpi.id ? { ...k, responsibleUnits: vals } : k))}
                          />
                        </View>
                      </View>

                      {/* Measurement + annual target grid — shared section for new AND existing KPIs */}
                      <KpiAnnualTargetSection
                        config={kpi.target}
                        onChange={(patch) => setKpis(kpis.map(k => k.id === kpi.id ? { ...k, target: { ...k.target, ...patch } } : k))}
                        unit={kpi.unit}
                        frequency={kpi.frequency}
                        startDate={kpi.measurementStartDate}
                        formula={kpi.formula}
                        dataSource={kpi.dataSource}
                        onMeasurementChange={(field, value) =>
                          handleKPIChange(kpi.id, field === 'startDate' ? 'measurementStartDate' : field, value)
                        }
                      />
                    </View>
                  )}
                </View>

                {/* Drag Handle */}
                <View style={styles.dragHandle}>
                  <MenuIcon size={16} color={tokens.colors.semantic.general.foreground} />
                </View>
              </View>
            ))}

            <TouchableOpacity style={[styles.addKpiButton, { backgroundColor: themeColors.addButton, borderColor: themeColors.addButtonBorder }]} onPress={handleAddKPI}>
              <Text style={[styles.addKpiButtonText, { color: themeColors.addButtonText }]}>{t('kpis.addKPI')}</Text>
              <PlusIcon size={14} color={themeColors.iconColor} />
            </TouchableOpacity>
          </View>

          {/* Footer Buttons */}
          <View style={styles.footer}>
            <Button
              variant="primary"
              onPress={handleSave}
              loading={submitting}
            >
              {isEditMode ? t('common.update') : t('common.save')}
            </Button>
            <Button variant="secondary" onPress={requestCancel}>
              {t('common.cancel')}
            </Button>
          </View>
          </View>
        </View>
      </ScrollView>
      <KPIDeleteConfirmModal
        visible={kpiPendingDelete !== null}
        kpiId={kpiPendingDelete?.backendId ?? null}
        kpiName={kpiPendingDelete?.name}
        onConfirm={confirmRemoveKPI}
        onCancel={() => setKpiPendingDelete(null)}
      />
      <CancelConfirmModal
        visible={confirmVisible}
        onConfirm={confirmDiscard}
        onClose={dismissConfirm}
      />
    </PageContainer>
  );
}

const getStyles = (isRTL: boolean) => StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  container: {
    flex: 1,
    padding: tokens.spacing.absolute["6"],
    paddingTop: tokens.spacing.absolute["4"],
  },
  containerSmall: {
    padding: tokens.spacing.absolute["4"],
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: tokens.spacing.absolute["3"],
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: COLORS.neutral600,
  },

  // Breadcrumb
  breadcrumb: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    marginBottom: tokens.spacing.absolute["4"],
    gap: tokens.spacing.absolute["2"],
  },
  breadcrumbItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  breadcrumbText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
  },
  breadcrumbActive: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: COLORS.primary,
  },

  // Main Card
  mainCard: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-12"],
    padding: tokens.spacing.absolute["6"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
    overflow: 'visible',
  },

  // Title
  pageTitle: {
    fontSize: tokens.typography["heading 4"]["font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["6"],
  },

  // Section
  section: {
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-12"],
    padding: tokens.spacing.absolute["5"],
    marginBottom: tokens.spacing.absolute["6"],
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginBottom: tokens.spacing.absolute["4"],
  },
  sectionTitle: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["4"],
  },

  // Form
  formRow: {
    flexDirection: 'row-reverse',
    gap: tokens.spacing.absolute["4"],
    marginBottom: tokens.spacing.absolute["4"],
  },
  formRowSmall: {
    flexDirection: 'column',
  },
  formGroup: {
    marginBottom: tokens.spacing.absolute["4"],
  },
  formGroupHalf: {
    flex: 1,
  },
  label: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["2"],
  },
  required: {
    color: tokens.colors.raw.red["500"],
  },
  inheritedLabel: {
    color: tokens.colors.raw.slate["500"],
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
  },
  input: {
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: 7.5,
    minHeight: 36,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    writingDirection: 'rtl',
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
    elevation: 1,
  },
  inputError: {
    borderColor: tokens.colors.raw.red["500"],
  },
  textArea: {
    minHeight: 60,
    textAlignVertical: 'top',
  },
  // Input with button (for اختيار مسؤول)
  inputWithButton: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    overflow: 'hidden',
  },
  inputWithButtonField: {
    flex: 1,
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    writingDirection: 'rtl',
    borderWidth: 0,
  },
  selectResponsibleButton: {
    backgroundColor: tokens.colors.raw.slate["100"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    borderStartWidth: tokens.spacing.absolute["px"],
    borderStartColor: tokens.colors.raw.gray["200"],
  },
  selectResponsibleButtonText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: '#0F172A',
  },

  // Dropdown
  dropdown: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: 7.5,
    minHeight: 36,
    gap: tokens.spacing.absolute["2"],
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
    elevation: 1,
  },
  dropdownDisabled: {
    opacity: 0.5,
  },
  dropdownText: {
    flex: 1,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["400"],
    textAlign: getTextAlign(isRTL),
    writingDirection: 'rtl',
  },
  dropdownTextSelected: {
    color: tokens.colors.semantic.general.foreground,
  },
  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    start: 0,
    end: 0,
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    marginTop: tokens.spacing.absolute["1"],
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
    elevation: 10,
    zIndex: 9999,
    maxHeight: 200,
    overflow: 'hidden',
  },
  dropdownItem: {
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.gray["100"],
  },
  dropdownItemText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    writingDirection: 'rtl',
  },
  dropdownContent: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    flex: 1,
    gap: tokens.spacing.absolute["2"],
  },
  dropdownPlaceholder: {
    color: tokens.colors.raw.gray["400"],
  },
  inlineDropdownOptions: {
    marginTop: tokens.spacing.absolute["2"],
    backgroundColor: tokens.colors.raw.gray["50"],
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
    maxHeight: 150,
    overflow: 'hidden',
  },
  inlineDropdownOption: {
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.gray["200"],
  },
  inlineDropdownOptionSelected: {
    backgroundColor: tokens.colors.raw.red["50"],
  },
  inlineDropdownOptionText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  inlineDropdownOptionTextSelected: {
    color: tokens.colors.semantic.general["Main Color"],
    fontFamily: FONTS.bold,
  },
  dropdownGroupLabel: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.bold,
    color: tokens.colors.raw.gray["500"],
    textAlign: getTextAlign(isRTL),
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingTop: tokens.spacing.absolute["2_5"],
    paddingBottom: tokens.spacing.absolute["1"],
    backgroundColor: tokens.colors.raw.gray["100"],
  },
  noDataText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["400"],
    textAlign: 'center',
    paddingVertical: tokens.spacing.absolute["4"],
  },

  // Searchable Multi-Select Dropdown Styles
  searchableDropdownMenu: {
    position: 'absolute',
    top: '100%',
    end: 0,
    start: 0,
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    marginTop: tokens.spacing.absolute["1"],
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
    elevation: 10,
    zIndex: 9999,
    maxHeight: 280,
  },
  dropdownSearchContainer: {
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2"],
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.gray["200"],
  },
  dropdownSearchInput: {
    backgroundColor: tokens.colors.raw.gray["50"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
    borderRadius: tokens.radii.absolute["radius-6"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2"],
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  dropdownOptionsList: {
    maxHeight: 200,
  },
  dropdownOptionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.gray["100"],
    gap: tokens.spacing.absolute["2_5"],
  },
  dropdownOptionItemSelected: {
    backgroundColor: tokens.colors.raw.red["50"],
  },
  optionCheckbox: {
    width: 18,
    height: 18,
    borderRadius: tokens.radii.absolute["radius-4"],
    borderWidth: 2,
    borderColor: tokens.colors.raw.gray["300"],
    backgroundColor: tokens.colors.raw.white,
    alignItems: 'center',
    justifyContent: 'center',
  },
  optionCheckboxSelected: {
    backgroundColor: tokens.colors.semantic.general["Main Color"],
    borderColor: tokens.colors.semantic.general["Main Color"],
  },
  optionCheckmark: {
    color: tokens.colors.raw.white,
    fontSize: tokens.typography.paragraph["paragraph caption font size"],
    fontWeight: 'bold',
  },
  dropdownOptionText: {
    flex: 1,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  dropdownOptionTextSelected: {
    color: tokens.colors.semantic.general["Main Color"],
    fontFamily: FONTS.bold,
  },
  noOptionsText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["400"],
    textAlign: 'center',
    paddingVertical: tokens.spacing.absolute["5"],
  },
  employeeOptionInfo: {
    flex: 1,
    alignItems: 'flex-end',
  },
  employeeOptionPosition: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
    textAlign: getTextAlign(isRTL),
    marginTop: tokens.spacing.absolute["0_5"],
  },
  selectedTagsContainer: {
    flexDirection: 'row-reverse',
    flexWrap: 'wrap',
    gap: tokens.spacing.absolute["2"],
    marginBottom: tokens.spacing.absolute["2"],
  },
  selectedTag: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: tokens.colors.raw.red["50"],
    borderRadius: tokens.radii.absolute["radius-16"],
    paddingVertical: tokens.spacing.absolute["1"],
    paddingStart: tokens.spacing.absolute["2"],
    paddingEnd: tokens.spacing.absolute["3"],
    gap: tokens.spacing.absolute["1_5"],
  },
  selectedTagText: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general["Main Color"],
  },
  removeTagButton: {
    width: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: tokens.colors.semantic.general["Main Color"],
    alignItems: 'center',
    justifyContent: 'center',
  },
  removeTagText: {
    color: tokens.colors.raw.white,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontWeight: 'bold',
    lineHeight: 16,
  },

  // Step Card Container (with drag handle)
  stepCardContainer: {
    flexDirection: 'row-reverse',
    alignItems: 'flex-start',
    gap: tokens.spacing.absolute["3"],
    marginBottom: tokens.spacing.absolute["6"],
    overflow: 'visible' as const,
    position: 'relative' as const,
    zIndex: 2,
  },
  dragHandle: {
    paddingTop: tokens.spacing.absolute["6"],
  },
  // Step Card
  stepCard: {
    flex: 1,
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    overflow: 'visible',
    padding: tokens.spacing.absolute["3"],
    gap: tokens.spacing.absolute["6"],
  },
  stepHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  stepHeaderRight: {
    flex: 1,
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: tokens.spacing.absolute["3"],
  },
  stepHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2_5"],
  },
  stepIconButton: {
    width: 40,
    height: 40,
    minWidth: 40,
    minHeight: 40,
    borderRadius: tokens.radii.absolute["radius-8"],
    backgroundColor: 'rgba(51, 65, 85, 0.05)',
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    alignItems: 'center',
    justifyContent: 'center',
  },
  stepIconButtonDisabled: {
    opacity: 0.5,
  },
  stepNumber: {
    width: 40,
    height: 40,
    minWidth: 40,
    minHeight: 40,
    borderRadius: tokens.radii.absolute["radius-4"],
    backgroundColor: tokens.colors.raw.slate["200"],
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["1"],
  },
  stepNumberText: {
    fontSize: tokens.typography["heading 4"]["font size"],
    fontFamily: FONTS.medium,
    fontWeight: '700',
    color: tokens.colors.semantic.general.foreground,
    lineHeight: 26,
  },
  stepTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
  },
  stepCollapsedName: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  stepContent: {
    gap: tokens.spacing.absolute["6"],
    overflow: 'visible' as const,
  },
  removeButton: {
    padding: tokens.spacing.absolute["1"],
  },
  // Input with icon
  inputWithIcon: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: 7.5,
    gap: tokens.spacing.absolute["2"],
  },
  inputFlex: {
    flex: 1,
    borderWidth: 0,
    padding: tokens.spacing.absolute["0"],
    backgroundColor: 'transparent',
    textAlign: getTextAlign(isRTL),
    writingDirection: 'rtl',
  },
  // Input with suffix
  inputWithSuffix: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: 7.5,
    gap: tokens.spacing.absolute["2"],
  },
  inputSuffix: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.slate["500"],
  },
  // Input with button on right side (for تعيين الوزن)
  inputWithButtonRight: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2_5"],
  },
  inputWithButtonLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2_5"],
  },
  inputWithSuffixContainer: {
    flex: 1,
    flexDirection: 'row-reverse',
    alignItems: 'center',
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: 7.5,
    gap: tokens.spacing.absolute["2"],
  },
  inputDisabled: {
    opacity: 0.5,
  },
  inputSuffixLeft: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.slate["500"],
  },
  assignWeightButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["300"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: 7.5,
    minHeight: 36,
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  assignWeightButtonText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
  },
  // Field hint (for دورية التنفيذ)
  fieldHint: {
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["1"],
    marginTop: tokens.spacing.absolute["2"],
  },
  fieldHintText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  // File input
  fileInput: {
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: 7.5,
  },
  fileInputContent: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2"],
  },
  fileInputText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
  },
  fileInputButton: {
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: 3,
  },
  fileInputButtonText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: '#0F172A',
  },
  fileHelpText: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["1"],
    marginTop: tokens.spacing.absolute["2"],
  },
  fileHelpTextContent: {
    flex: 1,
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  // Form group third
  formGroupThird: {
    flex: 1,
  },
  textAreaSmall: {
    minHeight: 60,
    textAlignVertical: 'top',
  },

  // KPI Section Header
  kpiSectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["3"],
    marginBottom: tokens.spacing.absolute["4"],
  },
  sectionToggleButton: {
    width: 40,
    height: 40,
    borderRadius: tokens.radii.absolute["radius-8"],
    backgroundColor: 'rgba(51, 65, 85, 0.05)',
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    alignItems: 'center',
    justifyContent: 'center',
  },
  sectionTitleNoMargin: {
    flex: 1,
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  // KPI Card Container (with drag handle)
  kpiCardContainer: {
    flexDirection: 'row-reverse',
    alignItems: 'flex-start',
    gap: tokens.spacing.absolute["3"],
    marginBottom: tokens.spacing.absolute["3"],
  },
  // KPI Card
  kpiCard: {
    flex: 1,
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    overflow: 'hidden',
  },
  kpiHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: tokens.spacing.absolute["3"],
  },
  kpiHeaderRight: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: tokens.spacing.absolute["3"],
  },
  kpiHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2_5"],
  },
  kpiNumberBadge: {
    width: 40,
    height: 40,
    borderRadius: tokens.radii.absolute["radius-4"],
    backgroundColor: tokens.colors.raw.slate["200"],
    alignItems: 'center',
    justifyContent: 'center',
  },
  kpiNumberBadgeText: {
    fontSize: tokens.typography["heading 4"]["font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
  },
  kpiCollapsedName: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  kpiNumber: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: COLORS.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  kpiNumberText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.white,
  },
  kpiTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
  },
  kpiContent: {
    padding: tokens.spacing.absolute["3"],
    paddingTop: tokens.spacing.absolute["0"],
    backgroundColor: tokens.colors.raw.white,
    gap: 40,
    overflow: 'visible',
  },
  kpiSubsection: {
    backgroundColor: tokens.colors.raw.slate["100"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-4"],
    padding: tokens.spacing.absolute["6"],
    overflow: 'visible',
    zIndex: 1,
  },
  subsectionTitle: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.medium,
    fontWeight: '700',
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["6"],
  },

  // Add Button (for steps)
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colors.raw.slate["100"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingVertical: tokens.spacing.absolute["3"],
    gap: tokens.spacing.absolute["2"],
  },
  addButtonText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
  },

  // Add Step Button (styled per Figma)
  addStepButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["300"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: 7.5,
    minHeight: 36,
    gap: tokens.spacing.absolute["2"],
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  addStepButtonText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
  },

  // Add KPI Button (styled per Figma)
  addKpiButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["300"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: 7.5,
    minHeight: 36,
    gap: tokens.spacing.absolute["2"],
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  addKpiButtonText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
  },

  // Input with icon on left side (for date picker, unit selector)
  inputWithIconLeft: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: 7.5,
    gap: tokens.spacing.absolute["2"],
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
    elevation: 1,
  },

  // Footer
  footer: {
    flexDirection: 'row',
    direction: 'ltr' as any,
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: tokens.spacing.absolute["3"],
    marginTop: tokens.spacing.absolute["2"],
    marginBottom: tokens.spacing.absolute["6"],
  },
});
